import React,  { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  Card,
  Button,
  Grid,
  TextField,
  Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  getAffiliateProvidersApi,
  getStoreAffiliatesApi,
  addAffiliateToStoreApi,
} from 'api';
import { BackdropCircular, Breadcrumb } from 'components';
import Divider from '@material-ui/core/Divider';
import TermsAndConditions from './TermsAndConditions';
import BankingInformation from './BankingInformation';
import MerchantStatus from './MerchantStatus';


const defaultAffiliateData = {
  affiliate: '',
  store: '',
  customerComment: '',
  businessName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  emailAddress: '',
  routingNumber: '',
  accountNumber: '',
};

export default function Affiliates() {
  const history = useHistory();
  const [selectedAffiliate, setSelectedAffiliate] = useState<null|IAffiliateProvider>();
  const [activeModal, setActiveModal] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [affiliateData, setAffiliateData] = useState(defaultAffiliateData);
  const affiliatesCmsUrl = {
    revian: '/affiliates/cms/revian',
    aethern: '/affiliates/cms/aethern_beauty_program'
  };

  const dispatch = useDispatch();
  const {
    affiliateProviders = [],
    affiliateProvidersKeywords = [],
    loadingAffiliateProviders,
    storeAffiliates,
    loadingStoreAffiliates
  } = useSelector((state: IReduxState) => state.partners);
  const userStore = useSelector((state: IReduxState) => state.user?.profile?.stores?.[0]);

  useEffect(() => {
    getAffiliateProvidersApi({dispatch});
    getStoreAffiliatesApi({dispatch});
  }, []);

  const handleCloseModal = () => {
    setActiveModal('');
    setSelectedAffiliate(null);
    setAffiliateData(defaultAffiliateData);
  };

  return (
    <div className="flex-grow min-h-full-screen scroll-y">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Affiliates' }
          ]}
        />
      </div>
      <Card className="p-10 m-10">
        <div className="p-10 border-1-muted border-radius-8">
          <Autocomplete
            className="w-400"
            options={affiliateProvidersKeywords}
            onChange={(event, value: string) => setSelectedFilter(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                name="Filter by keywords"
                label="Filter by keywords"
              />
            )}
          />
          {affiliateProviders.map((affiliate) => {
            if (selectedFilter && !affiliate.keywords.includes(selectedFilter)) {
              return null;
            }

            const alreadySent = storeAffiliates.find((a) => a.affiliate === affiliate._id);

            return (
              <>
                <Divider className="mb-5 mt-5" />
                <Grid key={affiliate.name} container>
                  <Grid item xs={3} >
                    <img className="w-200 border-transparent border-radius-8" src={affiliate.image} title={affiliate.name} alt={affiliate.name} />
                  </Grid>
                  <Grid item xs={2} className="flex-column justify-center">
                    <div className="text-18 font-bold mb-1">{affiliate.name}</div>
                    <a className="mb-1" target="_blank" rel="noreferrer" href={`https://${affiliate.url}`}>
                      {affiliate.url}
                    </a>
                  </Grid>
                  <Grid item xs={1} className="flex-column justify-center">
                    <div className="font-bold">${affiliate.price.toFixed(2)}</div>
                    <div>{affiliate.pricePer}</div>
                  </Grid>
                  <Grid item xs={2} className="flex-column justify-center">
                    <div className="font-bold mb-1">Program keywoards</div>
                    <div>
                      {affiliate.keywords.map((key) => (
                        <Chip key={key} className="mr-2 mb-1" size="small" label={key} />
                      ))}
                    </div>
                  </Grid>
                  <Grid item xs={2} className="flex-column justify-center px-2">
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        history.push(`${affiliatesCmsUrl[affiliate.name.toLocaleLowerCase()]}`);
                      }}
                    >
                      Learn More
                    </Button>
                  </Grid>
                  <Grid item xs={2} className="flex-column justify-center">
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={Boolean(alreadySent)}
                      onClick={() => {
                        setSelectedAffiliate(affiliate);
                        setActiveModal('TermsAndConditions');
                        setAffiliateData((data) => ({...data, affiliate: affiliate._id, store: userStore._id}));
                      }}
                    >
                      {alreadySent ? 'Status: PENDING' : 'Join Program'}
                    </Button>
                  </Grid>
                </Grid>
              </>
            );
          })}
        </div>
      </Card>
      {activeModal === 'TermsAndConditions' &&
        <TermsAndConditions
          affiliate={selectedAffiliate}
          onClose={handleCloseModal}
          onConfirm={(customerComment) => {
            setAffiliateData((data) => ({...data, customerComment}));
            setActiveModal('BankingInformation');
          }}
        />}
      {activeModal === 'BankingInformation' &&
      <BankingInformation
        affiliateData={affiliateData}
        isOpen={activeModal === 'BankingInformation'}
        onClose={handleCloseModal}
        onConfirm={(affiliateData) => {
          addAffiliateToStoreApi({dispatch, affiliateData});
          setActiveModal('MerchantStatus');
        }}
      />}
      {activeModal === 'MerchantStatus' &&
      <MerchantStatus
        affiliate={selectedAffiliate}
        isOpen={activeModal === 'MerchantStatus'}
        onClose={handleCloseModal}
      />}
      <BackdropCircular loading={loadingAffiliateProviders || loadingStoreAffiliates} />
    </div>
  );
}
